/**
 * Proyecto: MOGOTAX-CONDUCTOR
 * Lista de FUECs del conductor
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  List,
  Card,
  Typography,
  Tag,
  Spin,
  Empty,
  Button,
  message,
  Input,
  Space,
} from "antd";
import {
  FileTextOutlined,
  CalendarOutlined,
  CarOutlined,
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import api from "../../../axiosConfig";

const { Title, Text } = Typography;
const { Search } = Input;

const FuecList = () => {
  const navigate = useNavigate();
  const [fuecs, setFuecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [downloading, setDownloading] = useState(null);

  const fetchFuecs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/conductor/fuecs");
      const data = Array.isArray(response.data) ? response.data : response.data.data || [];
      setFuecs(data);
    } catch (error) {
      console.error("Error al cargar FUECs:", error);
      message.error("Error al cargar la lista de FUECs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFuecs();
  }, [fetchFuecs]);

  const handleDownloadPdf = async (fuecId) => {
    setDownloading(fuecId);
    try {
      const response = await api.get(`/conductor/fuec/${fuecId}/pdf`, {
        responseType: "blob",
      });

      // Crear URL del blob y descargar
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `FUEC-${fuecId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success("FUEC descargado exitosamente");
    } catch (error) {
      console.error("Error al descargar FUEC:", error);
      message.error("Error al descargar el FUEC");
    } finally {
      setDownloading(null);
    }
  };

  const handleViewDetail = (fuecId) => {
    navigate(`/modules/fuec/${fuecId}`);
  };

  const getEstadoColor = (estado) => {
    const colores = {
      activo: "green",
      vigente: "green",
      vencido: "red",
      cancelado: "red",
      pendiente: "orange",
    };
    return colores[estado?.toLowerCase()] || "default";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const filteredFuecs = fuecs.filter((fuec) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      fuec.numero_fuec?.toLowerCase().includes(search) ||
      fuec.vehiculo_placa?.toLowerCase().includes(search) ||
      fuec.cliente_nombre?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh"
      }}>
        <Spin size="large" tip="Cargando FUECs..." />
      </div>
    );
  }

  return (
    <div style={{ padding: "16px" }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 16,
        flexWrap: "wrap",
        gap: 8,
      }}>
        <Title level={4} style={{ margin: 0 }}>
          <FileTextOutlined style={{ marginRight: 8 }} />
          Mis FUECs
        </Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchFuecs}
          loading={loading}
        >
          Actualizar
        </Button>
      </div>

      <Search
        placeholder="Buscar por número, placa o empresa..."
        allowClear
        enterButton={<SearchOutlined />}
        size="large"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 16 }}
      />

      {filteredFuecs.length === 0 ? (
        <Empty
          description={
            searchText
              ? "No se encontraron FUECs con ese criterio"
              : "No tienes FUECs registrados"
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <List
          dataSource={filteredFuecs}
          renderItem={(fuec) => (
            <Card
              key={fuec.id}
              style={{ marginBottom: 12 }}
              bodyStyle={{ padding: "12px 16px" }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <Text strong style={{ fontSize: 16 }}>
                      {fuec.numero_fuec || `FUEC #${fuec.id}`}
                    </Text>
                    <Tag color={getEstadoColor(fuec.estado)}>
                      {fuec.estado || "Activo"}
                    </Tag>
                  </div>

                  <Space direction="vertical" size={4}>
                    <Text type="secondary">
                      <CarOutlined style={{ marginRight: 8 }} />
                      {fuec.vehiculo_placa || "Sin placa"} - {fuec.vehiculo_marca || ""} {fuec.vehiculo_modelo || ""}
                    </Text>

                    <Text type="secondary">
                      <CalendarOutlined style={{ marginRight: 8 }} />
                      Vigencia: {formatDate(fuec.fecha_inicio_vigencia)} - {formatDate(fuec.fecha_fin_vigencia)}
                    </Text>

                    {fuec.cliente_nombre && (
                      <Text type="secondary">
                        Empresa: {fuec.cliente_nombre}
                      </Text>
                    )}
                  </Space>
                </div>

                <Space direction="vertical" size={8}>
                  <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={() => handleViewDetail(fuec.id)}
                    size="small"
                  >
                    Ver
                  </Button>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadPdf(fuec.id)}
                    loading={downloading === fuec.id}
                    size="small"
                  >
                    PDF
                  </Button>
                </Space>
              </div>
            </Card>
          )}
        />
      )}
    </div>
  );
};

export default FuecList;
